import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {FiAlertTriangle, FiClock, FiUser} from "react-icons/fi";

import styles from "./ExamConflictModal.module.css";
import {formatDateTime} from "../../../utils/formatDateTime";

// End time formatted as time-only, so a range reads "22.06.2026, 10:00 – 10:30" instead of
// repeating the full date twice. Exams effectively never cross midnight.
const formatTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("az-Latn-AZ", {hour: "2-digit", minute: "2-digit"});
};

/**
 * Shown when scheduling an exam fails because some chosen students are already booked into an
 * overlapping exam. Lists exactly who is busy and with which exam/when, and nudges the user to pick
 * a different start time (the primary action jumps focus back to the start-time field).
 */
const ExamConflictModal = ({isOpen, conflicts, onClose, onChangeTime}) => {
    const {t} = useTranslation();

    // One card per student, with all of that student's clashing exams listed underneath.
    const grouped = useMemo(() => {
        const map = new Map();
        (conflicts || []).forEach((c) => {
            const key = c.studentNumber || `${c.firstName || ""}-${c.lastName || ""}`;
            if (!map.has(key)) {
                map.set(key, {
                    studentNumber: c.studentNumber,
                    name: `${c.firstName || ""} ${c.lastName || ""}`.trim(),
                    exams: [],
                });
            }
            map.get(key).exams.push({
                title: c.examTitle,
                start: c.scheduledStartTime,
                end: c.scheduledEndTime,
            });
        });
        return [...map.values()];
    }, [conflicts]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.iconWrap}>
                        <FiAlertTriangle className={styles.icon} aria-hidden/>
                    </div>
                    <div className={styles.headerText}>
                        <h3 className={styles.title}>{t("pages.examWizard.step4.conflict.title")}</h3>
                        <p className={styles.subtitle}>
                            {t("pages.examWizard.step4.conflict.count", {count: grouped.length})}
                        </p>
                    </div>
                </div>

                <p className={styles.intro}>{t("pages.examWizard.step4.conflict.intro")}</p>

                <div className={styles.list}>
                    {grouped.map((g, i) => (
                        <div className={styles.studentCard} key={g.studentNumber || i}>
                            <div className={styles.studentHead}>
                                <span className={styles.avatar}><FiUser size={13} aria-hidden/></span>
                                <span className={styles.studentName}>{g.name || g.studentNumber}</span>
                                {g.studentNumber && g.name && (
                                    <span className={styles.studentNo}>· {g.studentNumber}</span>
                                )}
                            </div>
                            {g.exams.map((ex, j) => (
                                <div className={styles.examRow} key={j}>
                                    <FiClock className={styles.examClock} size={13} aria-hidden/>
                                    <span className={styles.examTitle}>{ex.title}</span>
                                    <span className={styles.examTime}>
                                        {formatDateTime(ex.start)} – {formatTime(ex.end)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className={styles.suggestion}>
                    <FiClock size={15} aria-hidden/>
                    <span>{t("pages.examWizard.step4.conflict.suggestion")}</span>
                </div>

                <div className={styles.actions}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose}>
                        {t("common.close")}
                    </button>
                    <button type="button" className={styles.confirmBtn} onClick={onChangeTime}>
                        <FiClock size={15}/> {t("pages.examWizard.step4.conflict.changeTime")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExamConflictModal;
